// Turning coordinates into words, from a static index.
//
// The default index is the published planet build at
// `https://osm.rangefind.dev` — 8.5M documents over 310 shards, read by
// HTTP range request. There is no geocoding *server* here: the browser
// fetches the byte ranges its own query needs and nothing else, which is
// the same property the rest of this product rests on. Nothing about a
// recipient's stop travels in a query string, because there is no query
// string.
//
// This is decoration, never load-bearing. A stop with no name is a stop
// with a coordinate, and every call here degrades to null rather than
// failing a screen. If the index is unreachable the app is unchanged
// except that places are called what the plan called them.

import { bundleUrl } from "./engine.js";

export const DEFAULT_SEARCH_BASE = "https://osm.rangefind.dev";

/**
 * Below this, a query with no completions is treated as "still typing"
 * rather than "specific text the sidecar does not know", and no search is
 * run. See `PlaceLookup.suggest`.
 */
const MIN_SEARCH_FALLBACK_CHARS = 12;

interface SearchEngine {
  search(params: Record<string, unknown>): Promise<unknown>;
  manifest?: unknown;
}

interface OsmBundle {
  reverseGeocodeOsm(
    engine: SearchEngine,
    params: Record<string, unknown>
  ): Promise<{ results?: PlaceResult[] }>;
  searchOsmQuery(
    engine: SearchEngine,
    params: Record<string, unknown>
  ): Promise<{ results?: PlaceResult[] }>;
  suggestOsmQuery(
    engine: SearchEngine,
    params: Record<string, unknown>
  ): Promise<{ suggestions?: RawSuggestion[] }>;
  resolveOsmSuggestion(
    engine: SearchEngine,
    suggestion: unknown
  ): Promise<{ results?: PlaceResult[] }>;
}

/** Whatever the index returned. Only `text` is ours to read. */
interface RawSuggestion {
  text?: string;
  selection?: { doc?: unknown };
  doc?: unknown;
  [key: string]: unknown;
}

interface PlaceResult {
  doc?: Record<string, unknown>;
  fields?: Record<string, unknown>;
  lat?: number;
  lon?: number;
  [key: string]: unknown;
}

export interface Place {
  label: string;
  lat: number | null;
  lon: number | null;
}

/** A completion offered while someone is still typing. */
export interface PlaceSuggestion {
  /** The line to show. */
  text: string;
  /** Opaque index state. Hand it straight back to `resolve`. */
  raw: unknown;
}

export interface PlaceLookup {
  /** What is at this coordinate, in words. Null when nothing is known. */
  nameFor(lat: number, lon: number): Promise<string | null>;
  /**
   * Completions for a partial address. **This is the call a search field
   * makes on every keystroke** — never `find`.
   *
   * The difference is not a micro-optimisation. A full search has to
   * consult every shard whose vocabulary could contain the words, and a
   * half-typed address is made of words like "15" and "r" that occur in
   * all of them: measured against the planet index, `find("15 r")` issued
   * **4,960 HTTP requests across 294 of 310 shards** and exhausted the
   * browser's connection pool, while the same prefix through here cost
   * **20 requests**. The suggest sidecar exists precisely so a prefix
   * never becomes a global fan-out.
   */
  suggest(
    query: string,
    near?: { lat: number; lon: number } | null
  ): Promise<PlaceSuggestion[]>;
  /** Turn a chosen suggestion into a point. Null when it resolves to nothing. */
  resolve(
    suggestion: PlaceSuggestion,
    near?: { lat: number; lon: number } | null
  ): Promise<Place | null>;
  /**
   * Free-text search over whole words, biased near a point when one is
   * given. Correct for *committed* text — a pasted address, a name picked
   * from a list — and wrong for a prefix somebody is still typing. See
   * `suggest`.
   */
  find(query: string, near?: { lat: number; lon: number } | null): Promise<Place[]>;
}

function textOf(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

/** A result's best human label, whatever shape the index put it in. */
function labelOf(result: PlaceResult): string | null {
  const doc = (result.doc ?? result.fields ?? result) as Record<string, unknown>;
  const name = textOf(doc.name) ?? textOf(doc.title);
  const street = textOf(doc.street) ?? textOf(doc.road);
  const number = textOf(doc.housenumber) ?? textOf(doc.house_number);
  const locality = textOf(doc.locality) ?? textOf(doc.city) ?? textOf(doc.municipality);
  if (name) return locality && name !== locality ? `${name}, ${locality}` : name;
  if (street) {
    const line = number ? `${number} ${street}` : street;
    return locality ? `${line}, ${locality}` : line;
  }
  return locality ?? textOf(doc.address) ?? null;
}

function numberOf(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function pointOf(result: PlaceResult): { lat: number | null; lon: number | null } {
  const doc = (result.doc ?? result.fields ?? result) as Record<string, unknown>;
  return {
    lat: numberOf(result.lat) ?? numberOf(doc.lat),
    lon: numberOf(result.lon) ?? numberOf(doc.lon)
  };
}

/**
 * Opens the place index lazily: nothing is fetched until something is
 * actually looked up, so a page that never asks for a name never pays
 * for the manifest.
 */
export function createPlaceLookup(options: {
  engineBaseUrl?: string;
  searchBaseUrl?: string | null;
} = {}): PlaceLookup {
  const searchBaseUrl = options.searchBaseUrl === undefined
    ? DEFAULT_SEARCH_BASE
    : options.searchBaseUrl;
  const engineBaseUrl = options.engineBaseUrl ?? "/rangefind/";
  const cache = new Map<string, string | null>();
  let opening: Promise<{ engine: SearchEngine; osm: OsmBundle } | null> | null = null;

  function open(): Promise<{ engine: SearchEngine; osm: OsmBundle } | null> {
    if (!searchBaseUrl) return Promise.resolve(null);
    if (opening) return opening;
    opening = (async () => {
      try {
        const [runtime, osm] = await Promise.all([
          import(/* @vite-ignore */ bundleUrl(engineBaseUrl, "runtime.browser.js")) as Promise<{
            createSearch(options: Record<string, unknown>): Promise<SearchEngine>;
          }>,
          import(/* @vite-ignore */ bundleUrl(engineBaseUrl, "osm.browser.js")) as Promise<OsmBundle>
        ]);
        const engine = await runtime.createSearch({
          baseUrl: searchBaseUrl.endsWith("/") ? searchBaseUrl : `${searchBaseUrl}/`
        });
        return { engine, osm };
      } catch {
        return null;
      }
    })();
    return opening;
  }

  return {
    async nameFor(lat, lon) {
      const key = `${lat.toFixed(5)},${lon.toFixed(5)}`;
      if (cache.has(key)) return cache.get(key) ?? null;
      const opened = await open();
      if (!opened) {
        cache.set(key, null);
        return null;
      }
      try {
        const response = await opened.osm.reverseGeocodeOsm(opened.engine, {
          lat,
          lon,
          radiusMeters: 120,
          size: 3
        });
        const first = response.results?.[0];
        const label = first ? labelOf(first) : null;
        cache.set(key, label);
        return label;
      } catch {
        cache.set(key, null);
        return null;
      }
    },
    async suggest(query, near = null) {
      const opened = await open();
      if (!opened || !query.trim()) return [];
      try {
        const response = await opened.osm.suggestOsmQuery(opened.engine, {
          // `q`, not `query` — the suggest lane reads a different field,
          // and passing the wrong one yields an empty prefix, zero
          // requests, and a field that silently never completes anything.
          q: query,
          size: 8,
          ...(near ? { near } : {})
        });
        const suggestions = (response.suggestions ?? [])
          .map(item => ({ text: textOf(item.text) ?? "", raw: item }))
          .filter(item => item.text.length > 0);
        if (suggestions.length > 0) return suggestions;

        // The sidecar completes *prefixes*, so a fully typed address with
        // its town on the end — "15 rue de la gare luxembourg" — matches
        // no completion and comes back empty. That is the one case where a
        // real search is both necessary and cheap, and the emptiness is
        // itself the evidence: every short prefix measured here returned
        // completions ("15 r" gave three), so nothing comes back only when
        // the text is long and specific. The length floor is a backstop
        // against a pathological index, not the actual guard.
        if (query.trim().length < MIN_SEARCH_FALLBACK_CHARS) return [];
        const found = await this.find(query, near);
        return found.map(place => ({ text: place.label, raw: { place } }));
      } catch {
        return [];
      }
    },
    async resolve(suggestion, near = null) {
      const opened = await open();
      if (!opened) return null;
      const raw = suggestion.raw as (RawSuggestion & { place?: Place }) | undefined;
      // The search fallback in `suggest` already resolved this one; asking
      // the index again would be a second search for an answer in hand.
      if (raw?.place) return raw.place;
      try {
        // A suggestion that names one document is already an answer: one
        // row hydrated, no search at all.
        if (raw && (raw.selection?.doc || raw.doc)) {
          const response = await opened.osm.resolveOsmSuggestion(opened.engine, raw);
          const first = response.results?.[0];
          if (first) {
            const point = pointOf(first);
            if (point.lat != null && point.lon != null) {
              return { label: labelOf(first) ?? suggestion.text, ...point };
            }
          }
        }
        // Otherwise it is completed text — a street, a locality — and a
        // full search on whole words is selective enough to be cheap.
        const found = await this.find(suggestion.text, near);
        return found[0] ?? null;
      } catch {
        return null;
      }
    },
    async find(query, near = null) {
      const opened = await open();
      if (!opened || !query.trim()) return [];
      try {
        const response = await opened.osm.searchOsmQuery(opened.engine, {
          query,
          size: 8,
          ...(near ? { near } : {})
        });
        return (response.results ?? [])
          .map(result => {
            const point = pointOf(result);
            return { label: labelOf(result) ?? query, lat: point.lat, lon: point.lon };
          })
          .filter(place => place.lat != null && place.lon != null);
      } catch {
        return [];
      }
    }
  };
}
